import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import { Link2 } from "lucide-react";
import { type FC, useMemo } from "react";
import { GoalDetailSolutionItem } from "web_myplann/Goals/Components/GoalDetailSolutionItem";
import { GoalDetailSolutionItemSkeleton } from "web_myplann/Goals/Components/GoalDetailSolutionItemSkeleton";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import type { GoalPortfolioResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AddRowButton } from "web_myplann/Shared/Components/AddRowButton";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";

type Props = {
	goal: GoalDetail;
	portfolios: GoalPortfolioResponseDTO[];
	readOnly?: boolean;
	onAddPin: () => void;
	onUnpin: (portfolioId: number) => void;
	unpinningId: number | null;
};

export const GoalDetailOwnFunds: FC<Props> = ({
	goal,
	portfolios,
	readOnly,
	onAddPin,
	onUnpin,
	unpinningId,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();

	const isEmpty = portfolios.length === 0;

	const portfolioItems = useMemo(
		() =>
			portfolios.map((portfolio, portfolioIndex, allPortfolios) =>
				portfolio.portfolioId === unpinningId ? (
					<GoalDetailSolutionItemSkeleton
						key={portfolio.portfolioId}
						hasDivider={portfolioIndex < allPortfolios.length - 1}
					/>
				) : (
					<GoalDetailSolutionItem
						key={portfolio.portfolioId}
						portfolio={portfolio}
						hasDivider={portfolioIndex < allPortfolios.length - 1}
						readOnly={readOnly}
						onUnpin={onUnpin}
					/>
				),
			),
		[portfolios, unpinningId, readOnly, onUnpin],
	);

	return (
		<CollapsibleSection
			title={t(Resources.Goals.Detail.OwnFunds.title, {
				amount: formatAmount(goal.ownFunds),
			})}
		>
			<Stack bgcolor={theme.palette.background.paper}>
				{isEmpty ? (
					<Stack
						direction="row"
						alignItems="flex-start"
						gap={1.5}
						px={1.6}
						py={2}
					>
						<Box
							width={32}
							height={32}
							borderRadius={theme.radii.lg}
							bgcolor={alpha(theme.palette.primary.main, 0.08)}
							display="flex"
							alignItems="center"
							justifyContent="center"
							flexShrink={0}
						>
							<Link2
								size={theme.iconSize.sm}
								color={theme.palette.primary.main}
							/>
						</Box>
						<Stack flex={1} minWidth={0}>
							<Typography variant="bodyMedium">
								{t(Resources.Goals.Detail.OwnFunds.emptyTitle)}
							</Typography>
							<Typography
								variant="caption"
								color={theme.palette.grey[500]}
								mt={0.4}
								lineHeight={1.4}
							>
								{t(Resources.Goals.Detail.OwnFunds.emptyDescription)}
							</Typography>
						</Stack>
					</Stack>
				) : (
					portfolioItems
				)}
				{!readOnly && (
					<AddRowButton
						label={t(Resources.Goals.Detail.OwnFunds.pinContract)}
						onClick={onAddPin}
					/>
				)}
			</Stack>
		</CollapsibleSection>
	);
};
